import * as Collapsible from '@radix-ui/react-collapsible'
import * as Popover from '@radix-ui/react-popover'
import { ChevronDown, LogOut, Menu, UserRound, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

import logo from '@/assets/images/svgs/logo-6bios.svg'
import { useAuth } from '@/contexts'
import { ProfileType } from '@/enum/profile-type'
import { OptionsLanguage } from '@/models/weglot'

import { ActiveLink } from './active-link/active-link'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import { Button } from './button'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './navigation-menu'
import { ScrollArea } from './scroll-area'

export function Navbar() {
  const {
    signed,
    nameUser,
    perfilImage,
    userProfiles,
    currentProfile,
    setCurrentProfile,
  } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const [openCollapsible, setOpenCollapsible] = useState(false)
  const [openPopover, setOpenPopover] = useState(false)
  const [languages, setLanguages] = useState<OptionsLanguage[]>([])
  const [currentLanguage, setCurrentLanguage] = useState<string>('')

  useEffect(() => {
    setOpenCollapsible(false)
  }, [pathname])

  function handleLogout() {
    sessionStorage.clear()
    window.location.reload()
    window.location.replace('/login')
  }

  function initWeglot() {
    const wg = window.Weglot

    if (wg?.options?.languages) {
      const availableLanguages = wg.options.languages
        .map((lang) => lang.language_to)
        .concat(wg.options.language_from)

      const listLanguages = availableLanguages.map((lang) => ({
        flag: lang,
        fullName: wg.getLanguageName(lang),
      }))

      setLanguages(listLanguages)

      const currentLanguage = wg.getCurrentLang()

      setCurrentLanguage(currentLanguage)

      wg.on('languageChanged', (lang: string) => {
        setCurrentLanguage(lang)
      })

      return true
    }

    return false
  }

  const retryLoadingWeglot = useCallback(() => {
    const maxAttempts = 5
    let attempts = 0

    const retryInterval = 250 // 250ms milissegundos

    if (initWeglot()) return

    attempts++

    if (attempts >= maxAttempts) {
      console.error('Weglot não carregou após várias tentativas.')
      return
    }

    setTimeout(retryLoadingWeglot, retryInterval)
  }, [])

  const createSwitcher = useCallback(() => {
    async function createLanguageSwitcher() {
      retryLoadingWeglot()

      const languageOptions = document.getElementById('language-options')

      if (!languageOptions) return

      const observer = new MutationObserver(() => {
        if (initWeglot()) {
          observer.disconnect()
        }
      })

      observer.observe(languageOptions, { childList: true, subtree: true })

      return () => {
        observer.disconnect()
      }
    }

    createLanguageSwitcher()
  }, [retryLoadingWeglot])

  useEffect(() => {
    createSwitcher()
  }, [createSwitcher])

  const flagsUrls: Record<string, string> = {
    pt: 'https://cdn.weglot.com/flags/circle/br.svg',
    en: 'https://cdn.weglot.com/flags/circle/gb.svg',
    es: 'https://cdn.weglot.com/flags/circle/es.svg',
    fr: 'https://cdn.weglot.com/flags/circle/fr.svg',
    zh: 'https://cdn.weglot.com/flags/circle/cn.svg',
    ko: 'https://cdn.weglot.com/flags/circle/kr.svg',
    tw: 'https://cdn.weglot.com/flags/circle/cn.svg',
  }

  const menuItems: { id: ProfileType; label: string; to: string }[] = [
    { id: ProfileType.Preserver, label: 'Conservador', to: '/dashboard' },
    { id: ProfileType.Sponsor, label: 'Patrocinador', to: '/dashboard' },
    { id: ProfileType.Agent, label: 'Agente', to: '/dashboard' },
    { id: ProfileType.Supervisor, label: 'Supervisor', to: '/dashboard' },
    {
      id: ProfileType.Admin,
      label: 'Backoffice',
      to: '/backoffice/properties',
    },
    {
      id: ProfileType.SuperAdmin,
      label: 'Backoffice',
      to: '/backoffice/properties',
    },
  ]

  return (
    <Collapsible.Root asChild open={openCollapsible}>
      <nav className="bg-monochromatic-200 shadow-nav sticky top-0 z-30 flex flex-col items-center justify-between px-12 py-3 text-sm lg:flex-row xl:h-24 2xl:h-28 2xl:px-16">
        <div className="flex w-full justify-between lg:w-auto">
          <NavLink to="/" data-testid="navbar-home-link">
            <img
              src={logo}
              alt=""
              className="size-14 mix-blend-multiply 2xl:size-16"
            />
          </NavLink>
          <Collapsible.Trigger
            className="focus-visible:outline-hidden lg:hidden"
            onClick={() => setOpenCollapsible(!openCollapsible)}
            data-testid="navbar-hamburger-button"
          >
            {openCollapsible ? <X /> : <Menu />}
          </Collapsible.Trigger>
        </div>
        <Collapsible.Content
          asChild
          forceMount
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top-full data-[state=open]:slide-in-from-top-1/2 duration-500 data-[state=closed]:hidden lg:data-[state=closed]:flex"
        >
          <div className="flex flex-col items-center py-8 lg:flex-row">
            <div className="flex flex-col items-center gap-6 lg:flex-row 2xl:gap-10">
              <ActiveLink to="/about" data-testid="navbar-about-link">
                Quem somos
              </ActiveLink>
              <ActiveLink to="/services" data-testid="navbar-services-link">
                Serviços
              </ActiveLink>
              <ActiveLink
                to="/technologies"
                data-testid="navbar-technologies-link"
              >
                Tecnologias
              </ActiveLink>
              <ActiveLink
                to="/publications"
                data-testid="navbar-publications-link"
              >
                Publicações
              </ActiveLink>

              <ActiveLink to="/guardians" data-testid="navbar-guardians-link">
                Nossos Guardiões
              </ActiveLink>

              <ActiveLink to="/contact" data-testid="navbar-contact-link">
                Contato
              </ActiveLink>
            </div>

            <div className="mx-5 my-5 flex flex-col gap-5 lg:flex-row xl:my-0 xl:gap-4 2xl:mx-10 2xl:gap-8">
              {signed ? (
                <ActiveLink to="/be-guardian" data-testid="navbar-contact-link">
                  Seja um guardião ambiental
                </ActiveLink>
              ) : (
                <Button
                  variant="secondary"
                  className="font-normal uppercase"
                  onClick={() => navigate('/be-guardian')}
                  data-testid="navbar-be-guardian-button"
                >
                  Seja um guardião ambiental
                </Button>
              )}
              {!signed && (
                <Button
                  className="font-normal uppercase"
                  onClick={() => navigate('/register')}
                  data-testid="navbar-signup-button"
                >
                  Cadastre-se
                </Button>
              )}
            </div>
            {!signed && (
              <ActiveLink
                to="/login"
                className="2xl:mr-6"
                data-testid="navbar-login-link"
              >
                Login
              </ActiveLink>
            )}
            {signed && (
              <Popover.Root open={openPopover} onOpenChange={setOpenPopover}>
                <Popover.Trigger
                  className="group mt-5 ml-5 flex items-center justify-center gap-3 lg:mt-0"
                  data-testid="navbar-profile-trigger"
                >
                  <Avatar
                    title={nameUser ?? ''}
                    className="inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-full align-middle select-none"
                  >
                    <AvatarImage
                      src={perfilImage ?? ''}
                      alt="Profile picture"
                      className="h-full w-full object-cover"
                    />
                    <AvatarFallback
                      delayMs={300}
                      className="inline-flex h-11 w-11 items-center justify-center overflow-hidden align-middle"
                    >
                      {nameUser && nameUser[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span
                    className="max-w-32 truncate font-bold text-zinc-900"
                    data-wg-notranslate
                  >
                    {nameUser}
                  </span>
                  <ChevronDown className="transition-all group-data-[state=open]:rotate-180" />
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content className="z-40 mt-5 w-full rounded-lg border bg-white p-4 font-semibold text-nowrap shadow-sm">
                    <div className="flex flex-col">
                      <div className="flex w-full items-end gap-2">
                        <UserRound size={20} />
                        <p className="h-5">Perfil</p>
                      </div>
                      <div className="my-2 flex h-px w-full bg-gray-300" />
                      {menuItems.map(
                        ({ id, label, to }) =>
                          userProfiles?.includes(id) && (
                            <NavLink
                              to={to}
                              key={id}
                              onClick={() => {
                                setOpenPopover(false)
                                setCurrentProfile(id)
                              }}
                              className={twMerge(
                                'hover:bg-bios-primary/10 flex cursor-pointer items-center gap-3 rounded-sm px-6 py-2',
                                pathname === to &&
                                  currentProfile === id &&
                                  'text-bios-primary',
                              )}
                              data-testid="navbar-profile-menu-item"
                            >
                              {label}
                            </NavLink>
                          ),
                      )}
                      <div className="my-2 flex h-px w-full bg-gray-300" />
                      <div
                        className="hover:text-bios-primary mt-2 flex cursor-pointer gap-3"
                        onClick={handleLogout}
                        data-testid="navbar-logout-button"
                      >
                        <LogOut size={20} />
                        <span className="h-5">Sair</span>
                      </div>
                    </div>
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            )}

            <NavigationMenu
              className="mt-4 shadow-md md:mt-0 md:ml-4"
              id="language-options"
            >
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger data-testid="navbar-language-trigger">
                    {currentLanguage && (
                      <div
                        className="flex items-center gap-2"
                        data-wg-notranslate
                      >
                        <img
                          className="size-5 object-cover"
                          src={flagsUrls[currentLanguage]}
                          alt={`Image from ${currentLanguage}`}
                        />
                        <span className="text-xs font-semibold uppercase">
                          {currentLanguage}
                        </span>
                      </div>
                    )}
                  </NavigationMenuTrigger>
                  <ScrollArea>
                    <NavigationMenuContent className="flex flex-col p-1 shadow-md 2xl:p-2">
                      {languages
                        .filter((l) => l.flag !== currentLanguage)
                        .map((lng) => {
                          return (
                            <NavigationMenuLink
                              aria-label={lng.flag}
                              href={`#Weglot-${lng.flag}`}
                              key={lng.flag}
                              data-wg-notranslate
                              className="flex w-16 cursor-pointer items-center justify-center gap-2 rounded-md py-2 transition-all duration-300 hover:bg-blue-600 hover:text-white"
                              data-testid="navbar-language-option-item"
                            >
                              <img
                                className="size-5 object-cover"
                                src={flagsUrls[lng.flag]}
                                alt={`Image from ${lng.flag}`}
                              />

                              <span className="text-xs font-semibold uppercase">
                                {lng.flag}
                              </span>
                            </NavigationMenuLink>
                          )
                        })}
                    </NavigationMenuContent>
                  </ScrollArea>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>
        </Collapsible.Content>
      </nav>
    </Collapsible.Root>
  )
}
